import Label from '../../Utils/FormInputs/Label';
import TextField from '../../Utils/FormInputs/TextField';
import React, { useState } from 'react';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Snackbar from '@material-ui/core/Snackbar';
import CircularProgress from '@material-ui/core/CircularProgress';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import SendIcon from '@material-ui/icons/Send';
import GavelIcon from '@material-ui/icons/Gavel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import { makeStyles } from '@material-ui/core/styles';
import { surgeryService } from '../../../services/surgeryService';

const PREAUTH_STEPS = ['Submitted', 'Under Review', 'Approved / Denied'];

const STATUS_COLORS = {
  pending: '#FF9800',
  under_review: '#2196F3',
  approved: '#4CAF50',
  denied: '#F44336',
};

const STATUS_LABELS = {
  pending: 'Pending',
  under_review: 'Under Review',
  approved: 'Approved',
  denied: 'Denied',
};


const useStyles = makeStyles((theme) => ({
  root: {
    margin: 22,
    padding: 20,
  },
  title: {
    fontSize: '1.35rem',
    fontWeight: 700,
    color: '#333',
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#333',
    marginTop: 24,
    marginBottom: 12,
  },
  form: {
    marginBottom: 24,
  },
  formField: {
    marginBottom: 16,
  },
  submitBtn: {
    textTransform: 'none',
    fontWeight: 500,
    marginTop: 8,
  },
  stepper: {
    padding: '16px 0',
    backgroundColor: 'transparent',
  },
  statusSection: {
    padding: '16px 20px',
    marginBottom: 16,
    borderRadius: 8,
    border: '1px solid #E0E0E0',
  },
  statusHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  },
  statusIcon: {
    fontSize: '1.5rem',
  },
  statusText: {
    fontSize: '1rem',
    fontWeight: 600,
  },
  detailRow: {
    display: 'flex',
    padding: '6px 0',
  },
  detailLabel: {
    width: 200,
    fontSize: '0.85rem',
    color: '#777',
    fontWeight: 500,
    flexShrink: 0,
  },
  detailValue: {
    fontSize: '0.85rem',
    color: '#333',
    flex: 1,
  },
  approvedAmount: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#2E7D32',
  },
  deniedReason: {
    fontSize: '0.85rem',
    color: '#C62828',
    backgroundColor: '#FFEBEE',
    padding: '8px 12px',
    borderRadius: 4,
    marginTop: 8,
  },
  appealBtn: {
    textTransform: 'none',
    fontWeight: 500,
    marginTop: 12,
  },
  pendingText: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: '0.9rem',
    color: '#E65100',
    fontWeight: 500,
  },
  table: {
    marginTop: 12,
  },
  tableHead: {
    backgroundColor: '#F5F5F5',
  },
  headCell: {
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#555',
  },
  bodyCell: {
    fontSize: '0.85rem',
    color: '#333',
  },
  statusChip: {
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: 40,
  },
}));

const InsurancePreAuth = ({ surgeryId }) => {
  const classes = useStyles();

  const [formData, setFormData] = useState({
    insurance_company: '',
    policy_number: '',
    member_id: '',
    estimated_amount: '',
    procedure_description: '',
    clinical_justification: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await surgeryService.requestPreAuth({
        surgery_id: surgeryId,
        ...formData,
        estimated_amount: Number(formData.estimated_amount),
      });
      const data = res?.data || res;
      setSubmitted(data);
      setSnackbar({ open: true, message: 'Pre-authorization request submitted successfully.' });
    } catch (err) {
      console.error('Failed to submit pre-auth:', err);
      setSnackbar({ open: true, message: 'Failed to submit pre-authorization request.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAppeal = () => {
    setSnackbar({ open: true, message: 'Appeal process initiated. You will be contacted by the insurer.' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '' });
  };

  const getActiveStep = (status) => {
    if (status === 'pending') return 0;
    if (status === 'under_review') return 1;
    if (status === 'approved' || status === 'denied') return 2;
    return 0;
  };

  const renderStatusTracker = (preauth) => {
    const activeStep = getActiveStep(preauth.status);
    const statusColor = STATUS_COLORS[preauth.status] || '#999';

    return (
      <div className={classes.statusSection}>
        <div className={classes.statusHeader}>
          {preauth.status === 'approved' && (
            <CheckCircleIcon className={classes.statusIcon} style={{ color: '#4CAF50' }} />
          )}
          {preauth.status === 'denied' && (
            <CancelIcon className={classes.statusIcon} style={{ color: '#F44336' }} />
          )}
          {preauth.status === 'pending' && (
            <HourglassEmptyIcon className={classes.statusIcon} style={{ color: '#FF9800' }} />
          )}
          <Typography className={classes.statusText} style={{ color: statusColor }}>
            {STATUS_LABELS[preauth.status] || preauth.status}
          </Typography>
        </div>

        <Stepper activeStep={activeStep} className={classes.stepper} alternativeLabel>
          {PREAUTH_STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {preauth.status === 'approved' && (
          <>
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Approved Amount</Typography>
              <Typography className={classes.approvedAmount}>
                UGX {(preauth.approved_amount || 0).toLocaleString()}
              </Typography>
            </div>
            <div className={classes.detailRow}>
              <Typography className={classes.detailLabel}>Validity Period</Typography>
              <Typography className={classes.detailValue}>
                Valid until {preauth.validity || 'N/A'}
              </Typography>
            </div>
            {preauth.estimated_amount > (preauth.approved_amount || 0) && (
              <div className={classes.detailRow}>
                <Typography className={classes.detailLabel}>Remaining Balance</Typography>
                <Typography className={classes.detailValue} style={{ color: '#F44336', fontWeight: 600 }}>
                  UGX {(preauth.estimated_amount - (preauth.approved_amount || 0)).toLocaleString()}
                  {' '}(patient responsibility)
                </Typography>
              </div>
            )}
          </>
        )}

        {preauth.status === 'denied' && (
          <>
            <Typography className={classes.deniedReason}>
              Reason: {preauth.reason || 'No reason provided'}
            </Typography>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<GavelIcon />}
              className={classes.appealBtn}
              onClick={handleAppeal}
            >
              File Appeal
            </Button>
          </>
        )}

        {preauth.status === 'pending' && (
          <Typography className={classes.pendingText}>
            <HourglassEmptyIcon fontSize="small" />
            Awaiting insurer response &mdash; submitted {preauth.submitted_at || 'recently'}
          </Typography>
        )}
      </div>
    );
  };

  return (
    <Paper className={classes.root} elevation={1}>
      <Typography className={classes.title}>Insurance Pre-Authorization</Typography>

      {/* Submission Form */}
      <Typography className={classes.sectionTitle}>New Pre-Authorization Request</Typography>
      <form className={classes.form} onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Insurance Company"
              variant="outlined"
              size="small"
              fullWidth
              value={formData.insurance_company}
              onChange={handleChange('insurance_company')}
              required
              className={classes.formField}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Policy Number"
              variant="outlined"
              size="small"
              fullWidth
              value={formData.policy_number}
              onChange={handleChange('policy_number')}
              required
              className={classes.formField}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Member ID"
              variant="outlined"
              size="small"
              fullWidth
              value={formData.member_id}
              onChange={handleChange('member_id')}
              required
              className={classes.formField}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Estimated Amount (UGX)"
              variant="outlined"
              size="small"
              fullWidth
              type="number"
              value={formData.estimated_amount}
              onChange={handleChange('estimated_amount')}
              required
              className={classes.formField}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Procedure Description"
              variant="outlined"
              size="small"
              fullWidth
              value={formData.procedure_description}
              onChange={handleChange('procedure_description')}
              required
              className={classes.formField}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Clinical Justification"
              variant="outlined"
              size="small"
              fullWidth
              multiline
              rows={3}
              value={formData.clinical_justification}
              onChange={handleChange('clinical_justification')}
              required
              className={classes.formField}
            />
          </Grid>
        </Grid>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
          className={classes.submitBtn}
          disabled={submitting}
        >
          {submitting ? 'Submitting...' : 'Submit Pre-Authorization'}
        </Button>
      </form>

      {/* Status Tracker for newly submitted request */}
      {submitted && (
        <>
          <Divider />
          <Typography className={classes.sectionTitle}>Current Request Status</Typography>
          {renderStatusTracker({
            ...submitted,
            submitted_at: new Date().toLocaleString(),
          })}
        </>
      )}

      {/* Previously Submitted Pre-Auths */}
      <Divider />
      <Typography className={classes.sectionTitle}>Previous Pre-Authorization Requests</Typography>

      <Typography variant="body2" style={{ color: '#999', fontStyle: 'italic', padding: '8px 0' }}>
        No pre-authorisation requests have been submitted for this surgery.
      </Typography>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
};

export default InsurancePreAuth;
